import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { Loading, ErrorMessage } from '../components/Loading';
import './FriendMediaDetailsPage.css';

function FriendMediaDetailsPage() {
  const { friendId, mediaId } = useParams();
  const navigate = useNavigate();
  const [friendData, setFriendData] = useState(null);
  const [media, setMedia] = useState(null);
  const [alreadyInCollection, setAlreadyInCollection] = useState(false);
  const [existingMediaId, setExistingMediaId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addStatus, setAddStatus] = useState('quero_ver');
  const [adding, setAdding] = useState(false);

  const loadMediaDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Carregando detalhes:', { friendId, mediaId });
      
      // Carregar filme do amigo E sua coleção em paralelo
      const [friendMediaResponse, myMediaResponse] = await Promise.all([
        api.get(`/friends/${friendId}/media/${mediaId}`),
        api.get('/media')
      ]);
      
      console.log('Resposta recebida:', friendMediaResponse.data);
      setFriendData(friendMediaResponse.data.data.friend);
      setMedia(friendMediaResponse.data.data.media);
      
      // Verificar se você já tem este filme na sua coleção
      const friendMovie = friendMediaResponse.data.data.media;
      const myMovies = myMediaResponse.data.data;
      
      const existingMovie = myMovies.find(item => {
        // Verificar por IMDB ID (mais confiável)
        if (friendMovie.imdb_id && item.imdb_id === friendMovie.imdb_id) {
          return true;
        }
        // Fallback: verificar por título + ano
        const titleMatch = item.title.toLowerCase() === friendMovie.title.toLowerCase();
        const yearMatch = item.year === friendMovie.year;
        return titleMatch && yearMatch;
      });
      
      if (existingMovie) {
        setAlreadyInCollection(true);
        setExistingMediaId(existingMovie.id);
      } else {
        setAlreadyInCollection(false);
        setExistingMediaId(null);
      }
      
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      console.error('Erro response:', err.response);
      if (err.response?.status === 403) {
        setError('Você não tem permissão para ver este filme');
      } else if (err.response?.status === 404) {
        setError('Filme não encontrado');
      } else {
        setError(`Erro ao carregar detalhes do filme: ${err.response?.data?.error || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [friendId, mediaId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadMediaDetails();
  }, [loadMediaDetails]);

  const handleAddToCollection = async () => {
    if (!media) return;

    setAdding(true);
    try {
      const mediaToAdd = {
        ...media,
        status: addStatus,
        notes: media.notes ? `${media.notes}\n\nAdicionado da coleção de ${friendData.name}.` : `Adicionado da coleção de ${friendData.name}.`,
        id: undefined,
        user_id: undefined,
        date_added: undefined,
        date_watched: addStatus === 'ja_vi' ? new Date().toISOString().split('T')[0] : undefined,
      };

      const response = await api.post('/media', mediaToAdd);
      alert(`"${media.title}" foi adicionado à sua coleção com sucesso!`);
      navigate(`/details/${response.data.data.id}`);
    } catch (err) {
      console.error('Erro ao adicionar filme:', err);
      console.error('Erro response:', err.response);
      
      if (err.response?.status === 409) {
        const existingMedia = err.response?.data?.data;
        
        if (existingMedia && existingMedia.id) {
          const statusLabels = {
            'quero_ver': 'Quero Ver',
            'assistindo': 'Assistindo',
            'rever': 'Quero Ver Novamente',
            'ja_vi': 'Já Vi'
          };
          const statusText = statusLabels[existingMedia.status] || existingMedia.status || 'status desconhecido';
          
          if (window.confirm(`"${media.title}" já existe na sua coleção com o status "${statusText}". Deseja visualizá-lo?`)) {
            navigate(`/details/${existingMedia.id}`);
          }
        } else {
          alert(`"${media.title}" já existe na sua coleção.`);
        }
      } else {
        alert('Erro ao adicionar filme à sua coleção.');
      }
    } finally {
      setAdding(false);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= rating ? 'star filled' : 'star empty'}>
          {i <= rating ? '★' : '☆'}
        </span>
      );
    }
    return stars;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'quero_ver': 'Quero Ver',
      'assistindo': 'Assistindo',
      'rever': 'Quero Ver Novamente',
      'ja_vi': 'Já Vi'
    };
    return statusLabels[status] || status;
  };

  const getTypeLabel = (type) => {
    return type === 'movie' ? 'Filme' : 'Série';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  if (loading) return <Loading />;
  if (error) return <ErrorMessage message={error} />;
  if (!media) return <ErrorMessage message="Filme não encontrado" />;

  return (
    <div className="friend-media-details-page">
      <div className="details-header">
        <button onClick={() => navigate(`/friend/${friendId}`)} className="btn-back">
          ← Voltar para Coleção de {friendData?.name}
        </button>
        <span className="readonly-badge">👀 Visualizando Filme de {friendData?.name}</span>
      </div>

      <div className="details-content">
        <div className="details-poster">
          {media.poster_url ? (
            <img src={media.poster_url} alt={media.title} />
          ) : (
            <div className="no-poster">
              <span>🎬</span>
              <p>Sem poster</p>
            </div>
          )}
        </div>

        <div className="details-info">
          <div className="details-title-section">
            <h1>{media.title}</h1>
            <div className="details-meta">
              <span className={`type-badge ${media.type}`}>{getTypeLabel(media.type)}</span>
              <span className={`status-badge ${media.status}`}>{getStatusLabel(media.status)}</span>
            </div>
          </div>

          <div className="details-sections">
            {/* Informações Básicas */}
            <div className="info-section">
              <h3>📋 Informações</h3>
              <div className="info-grid">
                {media.year && (
                  <div className="info-item">
                    <span className="info-label">📅 Ano</span>
                    <span className="info-value">{media.year}</span>
                  </div>
                )}
                {media.runtime && (
                  <div className="info-item">
                    <span className="info-label">⏱️ Duração</span>
                    <span className="info-value">{media.runtime}</span>
                  </div>
                )}
                {media.country && (
                  <div className="info-item">
                    <span className="info-label">🌍 País</span>
                    <span className="info-value">{media.country}</span>
                  </div>
                )}
                {media.genre && (
                  <div className="info-item">
                    <span className="info-label">🎭 Gênero</span>
                    <span className="info-value">{media.genre}</span>
                  </div>
                )}
                {media.date_watched && (
                  <div className="info-item">
                    <span className="info-label">📆 {friendData?.name} Assistiu</span>
                    <span className="info-value">{formatDate(media.date_watched)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Avaliações */}
            {(media.rating || (media.imdb_rating && media.imdb_rating !== 'N/A')) && (
              <div className="info-section">
                <h3>⭐ Avaliações</h3>
                <div className="ratings-container">
                  {media.rating && (
                    <div className="rating-box">
                      <span className="rating-label">Nota de {friendData?.name}</span>
                      <div className="rating-stars">
                        {renderStars(media.rating)}
                      </div>
                    </div>
                  )}
                  {media.imdb_rating && media.imdb_rating !== 'N/A' && (
                    <div className="rating-box">
                      <span className="rating-label">IMDB</span>
                      <div className="rating-value">
                        <span className="star-icon">⭐</span>
                        <span className="rating-number">{media.imdb_rating}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sinopse */}
            {media.plot && media.plot !== 'N/A' && (
              <div className="info-section">
                <h3>📖 Sinopse</h3>
                <p className="plot-text">{media.plot}</p>
              </div>
            )}

            {/* Equipe */}
            {(media.director || media.actors) && (
              <div className="info-section">
                <h3>🎬 Equipe</h3>
                {media.director && (
                  <div className="crew-item">
                    <span className="crew-label">🎥 Direção:</span>
                    <span className="crew-value">
                      {media.director.split(',').map((director, index) => (
                        <span key={index}>
                          <Link to={`/person/${director.trim()}`} className="crew-link">
                            {director.trim()}
                          </Link>
                          {index < media.director.split(',').length - 1 && ', '}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
                {media.actors && (
                  <div className="crew-item">
                    <span className="crew-label">🎭 Elenco:</span>
                    <span className="crew-value">
                      {media.actors.split(',').map((actor, index) => (
                        <span key={index}>
                          <Link to={`/person/${actor.trim()}`} className="crew-link">
                            {actor.trim()}
                          </Link>
                          {index < media.actors.split(',').length - 1 && ', '}
                        </span>
                      ))}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Observações */}
            {media.notes && (
              <div className="info-section">
                <h3>📝 Observações de {friendData?.name}</h3>
                <p className="notes-text">{media.notes}</p>
              </div>
            )}

            {/* Adicionar à Coleção ou Já na Coleção */}
            <div className={`info-section ${alreadyInCollection ? 'already-in-collection-section' : 'add-section'}`}>
              {alreadyInCollection ? (
                <>
                  <h3>✓ Filme na sua Coleção</h3>
                  <div className="already-in-collection-info">
                    <div className="already-badge-large">
                      <span className="badge-icon-large">✓</span>
                      <div className="badge-content">
                        <span className="badge-title">Este filme já está na sua coleção!</span>
                        <span className="badge-subtitle">Você já adicionou este filme anteriormente</span>
                      </div>
                    </div>
                    <button 
                      className="btn-view-my-movie"
                      onClick={() => navigate(`/details/${existingMediaId}`)}
                    >
                      👁️ Ver Meu Filme
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3>➕ Adicionar à Minha Coleção</h3>
                  <div className="add-controls">
                    <label htmlFor="add-status">Escolha o status:</label>
                    <select 
                      id="add-status"
                      value={addStatus} 
                      onChange={(e) => setAddStatus(e.target.value)}
                      disabled={adding}
                    >
                      <option value="quero_ver">Quero Ver</option>
                      <option value="assistindo">Assistindo</option>
                      <option value="rever">Quero Ver Novamente</option>
                      <option value="ja_vi">Já Vi</option>
                    </select>
                    <button 
                      className="btn-add-collection"
                      onClick={handleAddToCollection}
                      disabled={adding}
                    >
                      {adding ? '⏳ Adicionando...' : '✓ Adicionar à Minha Coleção'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FriendMediaDetailsPage;
